import { ColDef, PostSortRowsParams, RowClickedEvent } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { useCallback, useMemo, useRef, useState } from "react";
import { aggridHeaderClass, SmallTitleSpan, titleMRClass } from "src/components/common/common";
import useUiuxStore from "src/store/useUiuxStore";
import { CreateCellClickHandler, createCellCssClassRules, CreateCellDoubleClickHandler, createGridDropDownCommitAfter, CustomCellClickAction, CustomCellDoubleClickAction, GridCellConfig, InsertRowHandler, postSortHandler, RemoveRowHandler, useCellChangeHandler, useRowSelectionHandler } from "src/utils/grid/GridHandlerContent";
import { useGridSettingContent } from "src/utils/grid/GridSettingContent";
import { defaultColDef, getDefaultRowIndexColumnConfig, getRowStyleFlags, IndexParameterCell } from "src/utils/grid/gridStyleUtils";
import { usePRD3010_R01Store } from "./Store_PRD3010_R01";

import { GridContextWrapper } from "src/components/common/gridWrapper/GridContextWrapper";
import { GridCustomColumnDefs } from "src/utils/grid/GridCustomColumnDefs";
import { buildDefaultColumnStateFromDefs } from "src/utils/grid/columnPinUtils";
import { defaultPRD3010_R01DetailDto, PRD3010_R01DetailDto, PRD3010_R01InspectDto } from "./Dto_PRD3010_R01";

import Grid_Excel from "src/components/common/gridWrapper/Grid_Excel";
import { useDetailTableHandlers as useCommonDetailTableHandlers } from "src/hooks/useDetailTableHandlers";

// 범용 훅 임포트
import { useDetailTableEvents } from "src/hooks/detail-table/useDetailTableEvents";
import { useCommonCodeStore } from "src/store/useCommonCodeStore";
import { createModuleConfig } from "src/utils/detail-table/detailTableUtils";
import { useMouseContent } from "src/utils/mouse/mouseContent";
import { calculateDetailAmount } from "src/utils/calculation/calculationUtils";
import { getAllWorkPlaces } from "src/apis/workPlaceApi";
import { InfoCodeSearchFieldConfig } from "src/types/info-form";
import { getAllWarehouses } from "src/apis/warehouseApi";
import { useInfoFormEvents } from "src/hooks/useInfoFormEvents";


const COLUMN_STATE_KEY = 'PRD3010_R01-detail-grid-column-state';

interface Props {
	gridRef: React.RefObject<AgGridReact<PRD3010_R01DetailDto> | null>;
	defaultPRD3010_R01DetailRow: PRD3010_R01DetailDto;
}

export const DetailTable_PRD3010_R01 = (props: Props) => {

	const { gridRef, defaultPRD3010_R01DetailRow } = props;

	const { 
			PRD3010_R01DetailList, setPRD3010_R01DetailList, 
			editPRD3010_R01,
			selectedPRD3010_R01DetailList, setSelectedPRD3010_R01DetailList, removeSelectedPRD3010_R01DetailList, addSelectedPRD3010_R01DetailList, initSelectedPRD3010_R01DetailList,
			deletedPRD3010_R01DetailList, setDeletedPRD3010_R01DetailList, addDeletedPRD3010_R01DetailList, removeDeletedPRD3010_R01DetailList,
			insertPRD3010_R01DetailList, updatePRD3010_R01DetailList, removePRD3010_R01DetailList,
			detailColumnState, setDetailColumnState,
			originalPRD3010_R01DetailList, setOriginalPRD3010_R01DetailList,
			detailScrollTop, setDetailScrollTop,
		} = usePRD3010_R01Store();
	const { setCodeSearchModalProps } = useUiuxStore();
	const { itemStatusList } = useCommonCodeStore();
	const [clickable, setClickable] = useState(true);

	//const gridRef = useRef<AgGridReact>(null);
	const gridWrapperRef = useRef<HTMLDivElement>(null); // 1. div를 위한 ref 생성

	const {
		getRowId,
		createRowClassRules,
		createCellClassRules,
		paddedGridData,
		renderIndexCell,
	} = useGridSettingContent({
		gridRef,
		key: 'valueIndex',
		gridData: PRD3010_R01DetailList,
		defaultGridData: defaultPRD3010_R01DetailRow,
		selectedRow: selectedPRD3010_R01DetailList,
		originalGridData: originalPRD3010_R01DetailList,
		updateRow: (index: number, data: any) => updatePRD3010_R01DetailList(index, data),
	});

	const cssCellConfig = useMemo<GridCellConfig>(() => ({	
	disabledCells: ['warehouseNm', 'childItemcd', 'itemNm', 'itemSpec', 'lotNo', 'lotQty', 'bomUsingQty'],
	insertedDisabledCells: [''],
	commonDisabledCells: ['', ],
	
	pointerCells: ['transportQty'],
	insertedPointerCells: [''],
	commonPointerCells: [''],
	alignLeftCells: ['', ],
}), []);

	const headerNames: Record<string, string> = {
		rowIndex: "",
		warehouseNm: '출고창고',
		itemCd: '자재코드',
		itemNm: '품명',
		itemSpec: '규격',
		lotNo: 'Lot No',
		lotQty: '재고량',
		bomUsingQty: '소요량',
		transportQty: '출고량',
	}

	const {
		disabledInsertedColumns, disabledColumns,
		pointerInsertedColumns, pointerColumns,
		alignLeftColumns,
	} = useMemo(() => createCellCssClassRules(cssCellConfig), [cssCellConfig]);

	const cellCssDefaultColDef = useMemo(() => {
        const baseRules = createCellClassRules<PRD3010_R01DetailDto>({
            idKey: 'valueIndex',
            selectedList: selectedPRD3010_R01DetailList,
            deletedList: deletedPRD3010_R01DetailList,
            disabledInsertedColumns,	disabledColumns,
            pointerInsertedColumns,		pointerColumns,
            alignLeftColumns,
        });

        return { ...defaultColDef, cellClassRules: { ...baseRules, }}; 
	}, [
        selectedPRD3010_R01DetailList, deletedPRD3010_R01DetailList,
        disabledInsertedColumns, disabledColumns,         
        pointerInsertedColumns, pointerColumns,
		alignLeftColumns,
    ]);	

	const commonInputColumns = ['transportQty', ];

	// 범용 이벤트 훅을 위한 설정
	const defaultEditableColumns = ['transportQty', ];


	const baseColumnDefs: ColDef<PRD3010_R01DetailDto>[] = useMemo(() => [
		...getDefaultRowIndexColumnConfig(
		),
		{
            headerName: '출고창고', colId: 'warehouseNm', field: 'warehouseNm', width: 80,
			editable: false,
		},
        {
            headerName: '자재코드', colId: 'childItemcd', field: 'childItemcd', width: 80,
			editable: false,
		},
		{
            headerName: '품명', colId: 'itemNm', field: 'itemNm', width: 150,
			editable: false,
		},
		{
            headerName: '규격', colId: 'itemSpec', field: 'itemSpec', width: 100,
			editable: false,
		},
		{
            headerName: 'Lot No', colId: 'lotNo', field: 'lotNo', width: 130,
			editable: false,
		},
		{
            headerName: '재고량', colId: 'lotQty', field: 'lotQty', width: 65,
			editable: false,
			comparator: (valueA, valueB) => Number(valueA) - Number(valueB),
			valueFormatter: (params) => params.value?.toLocaleString() ?? ''
		},
		// {
        //		headerName: '개당소요량', colId: 'childItemUsage', field: 'childItemUsage', width: 90,
		//		editable: false,
		// },
		// {
        //		headerName: '손실률', colId: 'lossPercent', field: 'lossPercent', width: 90,
		//		editable: false,
		// },
		{
            headerName: '소요량', colId: 'bomUsingQty', field: 'bomUsingQty', width: 65,
			editable: false,
			comparator: (valueA, valueB) => Number(valueA) - Number(valueB),
			valueFormatter: (params) => params.value?.toLocaleString() ?? ''
		},
		// {
        //		headerName: '출고완료량', colId: 'transportedQty', field: 'transportedQty', width: 65,
		//		editable: false,
		// },
		{
            headerName: '출고량', colId: 'transportQty', field: 'transportQty', width: 65,
			editable: true,
			comparator: (valueA, valueB) => Number(valueA) - Number(valueB),
			valueFormatter: (params) => params.value?.toLocaleString() ?? ''
		},
		// ...IndexParameterCell(),
	], [
		deletedPRD3010_R01DetailList, PRD3010_R01DetailList,
	]);

	const defaultColumnState = useMemo(
		() => buildDefaultColumnStateFromDefs(baseColumnDefs, []),
	[baseColumnDefs]);

	const columnDefs: ColDef<PRD3010_R01DetailDto>[] = useMemo(() => {
		if (detailColumnState === null) {
			return baseColumnDefs;
		}
		
		return GridCustomColumnDefs<PRD3010_R01DetailDto>({
			baseColumnDefs,
			columnState: detailColumnState,
			headerNames,
			fallbackHeaderClass: aggridHeaderClass
		});
	}, [baseColumnDefs, detailColumnState, headerNames]);

	
	const handleCellChange = useCellChangeHandler({
		dataList: PRD3010_R01DetailList,
		originalDataList: originalPRD3010_R01DetailList,
		updateRow: updatePRD3010_R01DetailList,
		customBeforeUpdate: (updatedData, field, value) => {
			return updatedData;
		},
		customAfterUpdate: (updatedData, field, value) => {
			if(field === 'transportQty') {
				if(updatedData.transportQty === 0) {
					updatedData.isModified = false;
				}
			}
			return updatedData;
		},
	});
	

	const handleRowClick = useRowSelectionHandler({
		dataList: PRD3010_R01DetailList,
		idKey: 'valueIndex',
		selectedList: selectedPRD3010_R01DetailList,
		deletedList: deletedPRD3010_R01DetailList,
		addSelected: addSelectedPRD3010_R01DetailList,
		removeSelected: removeSelectedPRD3010_R01DetailList,
		clickable, setClickable
	});


	// 범용 이벤트 훅 사용 (코드 검색 없이) - 방향키 이동 기능 유지
	/**
	 * 기본 상세 테이블 이벤트 핸들러 훅
	 * 
	 * 주요 기능:
	 * - 키다운 네비게이션 (방향키 이동)
	 * - 셀 클릭/키다운 이벤트 처리
	 * 
	 * @param detailList - 상세 데이터 목록
	 * @param editableColumns - 편집 가능한 컬럼 목록
	 */
	const cellEventHandlers = useDetailTableEvents({
		detailList: PRD3010_R01DetailList as any,
		editableColumns: defaultEditableColumns,
		keyDownConfig: {
			enableArrowNavigation: true,
		}
	});
	
	const cellClickActions: CustomCellClickAction<PRD3010_R01DetailDto>[] = useMemo(() => [
		{
			validate: (field, data) => {
				return commonInputColumns.includes(field);
			},
			//aggrid 기본 편집 기능
			action: (e) => { e.api.startEditingCell({ rowIndex: e.rowIndex!, colKey: e.colDef.field! }); }
		},
	], []);
	const handleCellClick = CreateCellClickHandler(PRD3010_R01DetailList, 'valueIndex', cellClickActions);

	//더블클릭 액션 없음
	const cellDoubleClickActions: CustomCellDoubleClickAction<PRD3010_R01DetailDto>[] = useMemo(() => [
	], [editPRD3010_R01?.itemCd]);
	const handleCellDoubleClick = CreateCellDoubleClickHandler(PRD3010_R01DetailList, 'valueIndex', cellDoubleClickActions);


	const handlePostSort = useCallback((params: PostSortRowsParams<PRD3010_R01DetailDto>) => {
		postSortHandler(params, PRD3010_R01DetailList, originalPRD3010_R01DetailList, setPRD3010_R01DetailList);
	}, [PRD3010_R01DetailList, originalPRD3010_R01DetailList, ]);

	const pinnedBottomRowData = useMemo(() => {
		return [{
			warehouseNm: PRD3010_R01DetailList.length.toLocaleString() + "건",
			// lotQty: PRD3010_R01DetailList.reduce((sum, item) => sum + (deletedPRD3010_R01DetailList.includes(item.valueIndex.toString()) ? 0 : Number(item.lotQty) || 0), 0),
		}];	  
	}, [PRD3010_R01DetailList, deletedPRD3010_R01DetailList,]);

	return (
		<>
			<div className="flex flex-1 h-full min-h-full">
				<GridContextWrapper<PRD3010_R01DetailDto>
					enableCellCalculation
					gridWrapperRef={gridWrapperRef}
					gridRef={gridRef}
					paddedGridData={paddedGridData}
					setPaddedGridData={setPRD3010_R01DetailList}
					initSelected={initSelectedPRD3010_R01DetailList}
					defaultRowData={defaultPRD3010_R01DetailRow}
					baseColumnDefs={detailColumnState ? columnDefs : baseColumnDefs}
			defaultColumnState={defaultColumnState}	
					setColumnState={setDetailColumnState}
					localStorageKey={COLUMN_STATE_KEY}
					menuItems={undefined}
				>
					<SmallTitleSpan>자재출고</SmallTitleSpan>	
					<Grid_Excel<PRD3010_R01DetailDto>
						ref={gridRef}
						rowData={paddedGridData}	getRowId={getRowId}
						columnDefs={columnDefs}		
						defaultColDef={cellCssDefaultColDef}

						rowClassRules={createRowClassRules<PRD3010_R01DetailDto>({
							idKey: 'valueIndex',
							selectedList: selectedPRD3010_R01DetailList,
							deletedList: deletedPRD3010_R01DetailList
						})}

						onRowClicked={handleRowClick}
						onCellClicked={handleCellClick}
						onCellDoubleClicked={handleCellDoubleClick}
						onCellKeyDown={cellEventHandlers.handleCellKeyDown}
						onCellValueChanged={handleCellChange}
						pinnedBottomRowData={pinnedBottomRowData}
						

						onPostSortRows={handlePostSort} //onSortChanged 삭제, 커스텀 정렬 기능추가 
						// Grid_Select에 공통 로직 처리를 위한 props 전달
						columnStateKey={COLUMN_STATE_KEY}			//컬럼 상태 키(로컬스토리지)	
						setColumnState={setDetailColumnState}		//컬럼 상태 저장
						getDefaultColumnState={() => defaultColumnState}
						scrollTop={detailScrollTop}
						setScrollTop={setDetailScrollTop}
					/>
				</GridContextWrapper>
			</div>
		</>
	)
}
